import { useCallback } from 'react';
import { NODE_TYPES } from '../constants';
import { calculateNodePosition, calculateSequentialPositions, scrollToNode } from '../utils/canvas';
import { getDefaultColor } from '../utils/colors';
import { normalizeNodeFlashcard } from '../utils/storage';

/**
 * Custom hook for node operations (create, update, delete, connect)
 * @param {Function} setNodes - Function to set nodes
 * @param {Function} setConnections - Function to set connections
 * @param {Object} scrollContainerRef - Ref to scroll container
 * @returns {Object} Node operation functions
 */
export const useNodeOperations = (setNodes, setConnections, scrollContainerRef) => {
  /**
   * Create a new node
   * @param {Object} data - Node data {type, title, summary}
   * @param {Array} existingNodes - Existing nodes
   * @param {Array} existingConnections - Existing connections
   * @param {Object} parentSolution - Parent solution node (optional)
   * @returns {Object} Created node
   */
  const createNode = useCallback((data, existingNodes, existingConnections, parentSolution = null) => {
    const nodeColor = parentSolution && parentSolution.color && parentSolution.type !== NODE_TYPES.PROBLEM
      ? parentSolution.color
      : getDefaultColor(data.type);

    const newNode = normalizeNodeFlashcard({
      id: Date.now().toString(),
      type: data.type,
      title: data.title,
      summary: data.summary,
      description: data.description || '',
      ...calculateNodePosition(data.type, existingNodes, existingConnections, parentSolution),
      banned: false,
      color: nodeColor,
      flashcardData: data.flashcardData || null
    });

    setNodes(prev => [...prev, newNode]);

    // Scroll to first node
    if (existingNodes.length === 0 && scrollContainerRef.current) {
      setTimeout(() => scrollToNode(newNode, scrollContainerRef.current), 50);
    }

    return newNode;
  }, [setNodes, scrollContainerRef]);

  /**
   * Update a node
   * @param {string} nodeId - Node ID
   * @param {Object} updates - Updates to apply
   */
  const updateNode = useCallback((nodeId, updates) => {
    setNodes(prev => prev.map(node => {
      if (node.id !== nodeId) return node;
      const mergedUpdates = updates.flashcardData
        ? { ...updates, flashcardData: { ...(node.flashcardData || {}), ...updates.flashcardData } }
        : updates;
      return normalizeNodeFlashcard({ ...node, ...mergedUpdates });
    }));
  }, [setNodes]);

  /**
   * Delete a node and its connections
   * @param {string} nodeId - Node ID
   * @param {Array} connections - Current connections
   * @returns {boolean} True if deletion was successful, false otherwise
   */
  const deleteNode = useCallback((nodeId, connections) => {
    // Check if node has any outgoing connections
    const hasOutgoingConnections = connections.some(conn => conn.from === nodeId);
    
    if (hasOutgoingConnections) {
      console.warn('Cannot delete a node that has outgoing connections');
      return false;
    }

    setNodes(prev => prev.filter(node => node.id !== nodeId));
    setConnections(prev => prev.filter(conn => conn.to !== nodeId));
    return true;
  }, [setNodes, setConnections]);

  /**
   * Toggle node banned status
   * @param {string} nodeId - Node ID
   */
  const toggleBan = useCallback((nodeId) => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, banned: !node.banned } : node
    ));
  }, [setNodes]);

  /**
   * Create a connection between two nodes
   * @param {string} fromId - Source node ID
   * @param {string} toId - Target node ID
   * @param {Array} nodes - Array of all nodes (for validation)
   */
  const createConnection = useCallback((fromId, toId, nodes) => {
    const fromNode = nodes.find(node => node.id === fromId);
    const toNode = nodes.find(node => node.id === toId);

    if (fromNode?.banned || toNode?.banned) {
      console.warn('Cannot connect to a banned node');
      return;
    }

    setConnections(prev => [...prev, {
      id: `${fromId}-${toId}`,
      from: fromId,
      to: toId,
      _animateIn: true
    }]);
  }, [setConnections]);

  /**
   * Delete a connection
   * @param {string} connectionId - Connection ID
   */
  const deleteConnection = useCallback((connectionId) => {
    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
  }, [setConnections]);

  /**
   * Create multiple nodes at once (for AI suggestions)
   * For problems: creates solutions in radial layout
   * For solutions: creates sequential steps in chain layout
   * @param {Array} solutions - Array of solution data
   * @param {Object} parentNode - Parent node (problem or solution)
   * @param {Array} existingNodes - Existing nodes
   * @param {Array} existingConnections - Existing connections
   * @returns {Array} Created nodes
   */
  const createMultipleNodes = useCallback((solutions, parentNode, existingNodes, existingConnections) => {
    let currentNodes = [...existingNodes];
    let currentConnections = [...existingConnections];
    const newConnections = [];
    const createdNodes = [];

    const hasExistingChildren = existingConnections.some(c => c.from === parentNode.id);
    const isSequential = parentNode.type === 'solution' && !hasExistingChildren;
    const positions = isSequential
      ? calculateSequentialPositions(parentNode, solutions.length, 'diagonal')
      : null;

    solutions.forEach((sol, index) => {
      const position = isSequential
        ? positions[index]
        : calculateNodePosition(NODE_TYPES.SOLUTION, currentNodes, currentConnections, parentNode);

      const nodeColor = parentNode.color && parentNode.type !== NODE_TYPES.PROBLEM
        ? parentNode.color
        : getDefaultColor(NODE_TYPES.SOLUTION);

      const newNode = normalizeNodeFlashcard({
        id: `${Date.now()}-${index}`,
        type: NODE_TYPES.SOLUTION,
        title: sol.title,
        summary: sol.summary,
        description: '',
        x: position.x,
        y: position.y,
        banned: false,
        color: nodeColor,
        flashcardData: sol.flashcardData || null
      });

      currentNodes = [...currentNodes, newNode];
      createdNodes.push(newNode);

      let conn;
      if (isSequential) {
        if (index === 0) {
          conn = { id: `${parentNode.id}-${newNode.id}`, from: parentNode.id, to: newNode.id, _animateIn: true };
        } else {
          conn = { id: `${createdNodes[index - 1].id}-${newNode.id}`, from: createdNodes[index - 1].id, to: newNode.id, _animateIn: true };
        }
      } else {
        conn = { id: `${parentNode.id}-${newNode.id}`, from: parentNode.id, to: newNode.id, _animateIn: true };
      }
      newConnections.push(conn);
      currentConnections = [...currentConnections, conn];
    });

    // Cross-link: se l'AI ha suggerito relatedTo, crea connessioni verso nodi esistenti
    const crossLinks = [];
    solutions.forEach((sol, index) => {
      if (!sol.relatedTo) return;
      const match = existingNodes.find(
        n => n.title.toLowerCase() === sol.relatedTo.toLowerCase()
      );
      if (match && !currentConnections.some(c => c.from === match.id && c.to === createdNodes[index].id)) {
        crossLinks.push({ id: `${match.id}-${createdNodes[index].id}`, from: match.id, to: createdNodes[index].id, _animateIn: true });
      }
    });

    setNodes(currentNodes);
    setConnections(prev => [...prev, ...newConnections, ...crossLinks]);

    return createdNodes;
  }, [setNodes, setConnections]);

  /**
   * Reset all nodes and connections
   */
  const resetAll = useCallback(() => {
    setNodes([]);
    setConnections([]);
  }, [setNodes, setConnections]);

  return {
    createNode,
    updateNode,
    deleteNode,
    toggleBan,
    createConnection,
    deleteConnection,
    createMultipleNodes,
    resetAll
  };
};
